import { useEffect, useRef, useCallback, useMemo } from 'react'
import type { Strategy, LegInfo } from '../api'

interface StrategyDetailModalProps {
  strategy: Strategy
  onClose: () => void
}

/* ─── Payoff math ─── */

interface PayoffPoint {
  price: number
  pnl: number
}

interface LegDisplay {
  action: 'BUY' | 'SELL'
  type: 'CALL' | 'PUT'
  strike: number
  premium: number
}

function getStrategyType(s: Strategy): string {
  return (s.strategy_type ?? '').toUpperCase().replace(/[^A-Z_]/g, '')
}

/**
 * Compute payoff directly from max_profit / max_loss / strategy type.
 * This avoids guessing individual leg premiums which led to wrong chart shapes.
 */
function computePayoffDirect(s: Strategy, price: number): number {
  const strike = s.strike ?? 0
  const maxProfit = s.max_profit ?? 0
  const maxLoss = s.max_loss ?? 0
  const netDebit = s.net_debit ?? 0
  const isCredit = s.is_credit ?? (netDebit < 0)
  const netCredit = s.net_credit ?? (isCredit ? -netDebit : 0)
  const t = getStrategyType(s)

  if (strike === 0) return 0

  // ── DEBIT SPREADS ──────────────────────────────────────────
  if (t === 'BULL_CALL_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    const K_low = strike
    const K_high = strike + spreadWidth
    if (price <= K_low) return -netDebit
    if (price >= K_high) return maxProfit
    return -netDebit + ((price - K_low) / spreadWidth) * (maxProfit + netDebit)
  }

  if (t === 'BEAR_PUT_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    const K_high = strike
    const K_low = strike - spreadWidth
    if (price <= K_low) return maxProfit
    if (price >= K_high) return -netDebit
    const ratio = (K_high - price) / spreadWidth
    return -netDebit + ratio * (maxProfit + netDebit)
  }

  // ── CREDIT SPREADS ────────────────────────────────────────
  if (t === 'BULL_PUT_SPREAD') {
    // Sell higher put (K_high), buy lower put (K_low)
    // Max profit = net credit (above K_high)
    // Max loss = (K_high - K_low) - net credit (below K_low)
    const spreadWidth = maxLoss + netCredit // K_high - K_low
    const K_high = strike // short put strike (primary leg)
    const K_low = K_high - spreadWidth
    if (price >= K_high) return netCredit    // max profit
    if (price <= K_low) return -maxLoss      // max loss
    // Linear between K_low and K_high
    return netCredit - ((K_high - price) / spreadWidth) * (netCredit + maxLoss)
  }

  if (t === 'BEAR_CALL_SPREAD') {
    // Sell lower call (K_low), buy higher call (K_high)
    // Max profit = net credit (below K_low)
    // Max loss = (K_high - K_low) - net credit (above K_high)
    const spreadWidth = maxLoss + netCredit // K_high - K_low
    const K_low = strike // short call strike (primary leg)
    const K_high = K_low + spreadWidth
    if (price <= K_low) return netCredit    // max profit
    if (price >= K_high) return -maxLoss    // max loss
    // Linear between K_low and K_high
    return netCredit - ((price - K_low) / spreadWidth) * (netCredit + maxLoss)
  }

  // ── 4-LEG CREDIT STRATEGIES ───────────────────────────────
  if (t === 'IRON_CONDOR') {
    // Use legs array for precise computation
    if (s.legs && s.legs.length === 4) {
      let payoff = 0
      for (const leg of s.legs) {
        const intrinsic = leg.type === 'CALL'
          ? Math.max(price - leg.strike, 0)
          : Math.max(leg.strike - price, 0)
        payoff += (leg.action === 'BUY' ? 1 : -1) * intrinsic
      }
      return payoff + netCredit // intrinsic + credit received
    }
    // Fallback approximation
    return maxProfit // simplified: flat at max profit in the middle
  }

  if (t === 'IRON_BUTTERFLY') {
    if (s.legs && s.legs.length === 4) {
      let payoff = 0
      for (const leg of s.legs) {
        const intrinsic = leg.type === 'CALL'
          ? Math.max(price - leg.strike, 0)
          : Math.max(leg.strike - price, 0)
        payoff += (leg.action === 'BUY' ? 1 : -1) * intrinsic
      }
      return payoff + netCredit
    }
    return maxProfit
  }

  // ── STRADDLE ─────────────────────────────────────────────
  if (t.includes('STRADDLE')) {
    return Math.abs(price - strike) - netDebit
  }

  // ── STRANGLE ─────────────────────────────────────────────
  if (t.includes('STRANGLE')) {
    const spreadWidth = maxProfit + netDebit
    const K_call = strike + spreadWidth / 2
    const K_put = strike - spreadWidth / 2
    return Math.max(price - K_call, 0) + Math.max(K_put - price, 0) - netDebit
  }

  // Generic fallback: single long call
  return Math.max(price - strike, 0) - netDebit
}

/** Build leg info for display cards — prefer backend per-leg data, fall back to heuristic */
function getLegs(s: Strategy): LegDisplay[] {
  // If backend provides per-leg data, use it directly (prices always positive)
  if (s.legs && s.legs.length > 0) {
    return s.legs.map((l: LegInfo) => ({
      action: l.action,
      type: l.type as 'CALL' | 'PUT',
      strike: l.strike,
      premium: l.entry_price, // always positive
    }))
  }

  // Fallback: compute from strategy metadata
  const strike = s.strike ?? 0
  const maxProfit = s.max_profit ?? 0
  const maxLoss = s.max_loss ?? 0
  const netDebit = s.net_debit ?? 0
  const isCredit = s.is_credit ?? (netDebit < 0)
  const netCredit = s.net_credit ?? (isCredit ? -netDebit : 0)
  const t = getStrategyType(s)

  if (t === 'BULL_CALL_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    return [
      { action: 'BUY', type: 'CALL', strike, premium: netDebit * 0.6 },
      { action: 'SELL', type: 'CALL', strike: strike + spreadWidth, premium: netDebit * 0.4 },
    ]
  }
  if (t === 'BEAR_PUT_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    return [
      { action: 'BUY', type: 'PUT', strike, premium: netDebit * 0.6 },
      { action: 'SELL', type: 'PUT', strike: strike - spreadWidth, premium: netDebit * 0.4 },
    ]
  }
  if (t === 'BULL_PUT_SPREAD') {
    const spreadWidth = maxLoss + netCredit
    return [
      { action: 'SELL', type: 'PUT', strike, premium: netCredit * 0.6 },
      { action: 'BUY', type: 'PUT', strike: strike - spreadWidth, premium: netCredit * 0.4 },
    ]
  }
  if (t === 'BEAR_CALL_SPREAD') {
    const spreadWidth = maxLoss + netCredit
    return [
      { action: 'SELL', type: 'CALL', strike, premium: netCredit * 0.6 },
      { action: 'BUY', type: 'CALL', strike: strike + spreadWidth, premium: netCredit * 0.4 },
    ]
  }
  if (t.includes('STRADDLE')) {
    const totalDebit = Math.abs(netDebit)
    return [
      { action: 'BUY', type: 'CALL', strike, premium: totalDebit / 2 },
      { action: 'BUY', type: 'PUT', strike, premium: totalDebit / 2 },
    ]
  }
  if (t.includes('STRANGLE')) {
    const totalDebit = Math.abs(netDebit)
    const spreadWidth = maxProfit + totalDebit
    return [
      { action: 'BUY', type: 'CALL', strike: strike + spreadWidth / 2, premium: totalDebit * 0.5 },
      { action: 'BUY', type: 'PUT', strike: strike - spreadWidth / 2, premium: totalDebit * 0.5 },
    ]
  }
  // 4-leg fallback
  return [{ action: 'BUY', type: 'CALL', strike, premium: Math.abs(netDebit) }]
}

function computePayoffCurve(s: Strategy): PayoffPoint[] {
  const strike = s.strike ?? 0
  const maxProfit = s.max_profit ?? 0
  const maxLoss = s.max_loss ?? 0
  const netDebit = s.net_debit ?? 0
  const isCredit = s.is_credit ?? (netDebit < 0)
  const netCredit = s.net_credit ?? (isCredit ? -netDebit : 0)
  const t = getStrategyType(s)
  if (strike === 0) return []

  let lo: number, hi: number

  if (t === 'BULL_CALL_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    lo = strike - spreadWidth * 0.3
    hi = strike + spreadWidth * 1.3
  } else if (t === 'BEAR_PUT_SPREAD') {
    const spreadWidth = maxProfit + netDebit
    lo = strike - spreadWidth * 1.3
    hi = strike + spreadWidth * 0.3
  } else if (t === 'BULL_PUT_SPREAD') {
    // Short put at strike (K_high), long put at K_low
    const spreadWidth = maxLoss + netCredit
    const K_high = strike
    const K_low = K_high - spreadWidth
    lo = K_low - spreadWidth * 0.3
    hi = K_high + spreadWidth * 0.3
  } else if (t === 'BEAR_CALL_SPREAD') {
    // Short call at strike (K_low), long call at K_high
    const spreadWidth = maxLoss + netCredit
    const K_low = strike
    const K_high = K_low + spreadWidth
    lo = K_low - spreadWidth * 0.3
    hi = K_high + spreadWidth * 0.3
  } else if (t === 'IRON_CONDOR' && s.legs && s.legs.length === 4) {
    const strikes = s.legs.map(l => l.strike).sort((a, b) => a - b)
    const wing = Math.max(strikes[1] - strikes[0], strikes[3] - strikes[2])
    lo = strikes[0] - wing * 0.5
    hi = strikes[3] + wing * 0.5
  } else if (t === 'IRON_BUTTERFLY' && s.legs && s.legs.length === 4) {
    const strikes = s.legs.map(l => l.strike).sort((a, b) => a - b)
    const wing = strikes[3] - strikes[0]
    lo = strikes[0] - wing * 0.3
    hi = strikes[3] + wing * 0.3
  } else if (t.includes('STRADDLE')) {
    const spreadWidth = maxProfit + Math.abs(netDebit)
    const margin = Math.abs(maxLoss) * 2 + spreadWidth * 0.5
    lo = strike - margin
    hi = strike + margin
  } else if (t.includes('STRANGLE')) {
    const spreadWidth = maxProfit + Math.abs(netDebit)
    const halfSpread = spreadWidth / 2
    const margin = Math.abs(maxLoss) * 1.5
    lo = strike - halfSpread - margin
    hi = strike + halfSpread + margin
  } else {
    const range = strike * 0.3
    lo = strike - range
    hi = strike + range
  }

  const steps = 200
  const points: PayoffPoint[] = []
  for (let i = 0; i <= steps; i++) {
    const price = lo + (i / steps) * (hi - lo)
    points.push({ price, pnl: computePayoffDirect(s, price) })
  }
  return points
}

function findBreakevens(points: PayoffPoint[]): number[] {
  const bes: number[] = []
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    if ((prev.pnl <= 0 && curr.pnl > 0) || (prev.pnl >= 0 && curr.pnl < 0)) {
      const t = Math.abs(prev.pnl) / (Math.abs(prev.pnl) + Math.abs(curr.pnl))
      bes.push(prev.price + t * (curr.price - prev.price))
    }
  }
  return bes
}

/* ─── SVG Payoff Chart ─── */

function PayoffChart({ strategy }: { strategy: Strategy }) {
  const points = useMemo(() => computePayoffCurve(strategy), [strategy])
  const breakevens = useMemo(() => findBreakevens(points), [points])

  if (points.length === 0) {
    return <div className="flex h-48 items-center justify-center text-sm text-gray-500">No payoff data</div>
  }

  const pad = { top: 20, right: 30, bottom: 40, left: 60 }
  const W = 560
  const H = 260
  const cw = W - pad.left - pad.right
  const ch = H - pad.top - pad.bottom

  const prices = points.map((p) => p.price)
  const pnls = points.map((p) => p.pnl)
  const minP = Math.min(...pnls)
  const maxP = Math.max(...pnls)
  const minPr = Math.min(...prices)
  const maxPr = Math.max(...prices)

  // Add padding to Y range
  const yPad = Math.max((maxP - minP) * 0.15, 10)
  const yMin = minP - yPad
  const yMax = maxP + yPad

  const xScale = (price: number) => pad.left + ((price - minPr) / (maxPr - minPr)) * cw
  const yScale = (pnl: number) => pad.top + ch - ((pnl - yMin) / (yMax - yMin)) * ch

  // Build SVG path
  const linePath = points.map((p, i) => {
    const x = xScale(p.price)
    const y = yScale(p.pnl)
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  // Area paths for profit/loss zones — properly segmented at zero crossings
  const zeroY = yScale(0)
  const profitAreaParts: string[] = []
  const lossAreaParts: string[] = []

  // Split points into segments at zero crossings
  type Seg = { points: PayoffPoint[]; sign: 'pos' | 'neg' | 'zero' }
  const segments: Seg[] = []
  let segPts: PayoffPoint[] = [points[0]]
  let segSign: 'pos' | 'neg' | 'zero' = points[0].pnl > 0 ? 'pos' : points[0].pnl < 0 ? 'neg' : 'zero'

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const crossed = (prev.pnl > 0 && curr.pnl < 0) || (prev.pnl < 0 && curr.pnl > 0)

    if (crossed) {
      // Compute interpolated crossing point
      const frac = Math.abs(prev.pnl) / (Math.abs(prev.pnl) + Math.abs(curr.pnl))
      const crossPrice = prev.price + frac * (curr.price - prev.price)
      segPts.push({ price: crossPrice, pnl: 0 })
      segments.push({ points: segPts, sign: segSign })
      segPts = [{ price: crossPrice, pnl: 0 }, curr]
      segSign = curr.pnl > 0 ? 'pos' : curr.pnl < 0 ? 'neg' : 'zero'
    } else {
      segPts.push(curr)
    }
  }
  segments.push({ points: segPts, sign: segSign })

  // Build closed area paths for each segment
  for (const seg of segments) {
    if (seg.sign === 'zero' || seg.points.length < 2) continue
    const fillArr = seg.sign === 'pos' ? profitAreaParts : lossAreaParts
    let d = `M${xScale(seg.points[0].price).toFixed(1)},${zeroY.toFixed(1)}`
    for (const pt of seg.points) {
      d += ` L${xScale(pt.price).toFixed(1)},${yScale(pt.pnl).toFixed(1)}`
    }
    d += ` L${xScale(seg.points[seg.points.length - 1].price).toFixed(1)},${zeroY.toFixed(1)} Z`
    fillArr.push(d)
  }

  const profitArea = profitAreaParts.join(' ')
  const lossArea = lossAreaParts.join(' ')

  // Y-axis ticks
  const yTicks = 5
  const yTickValues = Array.from({ length: yTicks }, (_, i) => yMin + (i / (yTicks - 1)) * (yMax - yMin))

  // X-axis ticks
  const xTicks = 6
  const xTickValues = Array.from({ length: xTicks }, (_, i) => minPr + (i / (xTicks - 1)) * (maxPr - minPr))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: '280px' }}>
      {/* Grid lines */}
      {yTickValues.map((v, i) => (
        <line
          key={`yg-${i}`}
          x1={pad.left}
          y1={yScale(v)}
          x2={W - pad.right}
          y2={yScale(v)}
          stroke="#1e293b"
          strokeWidth={v === 0 ? 1.5 : 0.5}
        />
      ))}

      {/* Profit zone fill */}
      {profitArea && <path d={profitArea} fill="rgba(16,185,129,0.12)" />}
      {/* Loss zone fill */}
      {lossArea && <path d={lossArea} fill="rgba(239,68,68,0.12)" />}

      {/* Zero line */}
      <line
        x1={pad.left}
        y1={yScale(0)}
        x2={W - pad.right}
        y2={yScale(0)}
        stroke="#4b5563"
        strokeWidth={1}
        strokeDasharray="4,3"
      />

      {/* Breakeven lines */}
      {breakevens.map((be, i) => (
        <g key={`be-${i}`}>
          <line
            x1={xScale(be)}
            y1={pad.top}
            x2={xScale(be)}
            y2={pad.top + ch}
            stroke="#fbbf24"
            strokeWidth={1}
            strokeDasharray="6,4"
          />
          <text
            x={xScale(be)}
            y={pad.top - 5}
            textAnchor="middle"
            fill="#fbbf24"
            fontSize={10}
            fontFamily="JetBrains Mono, monospace"
          >
            BE: ${formatK(be)}
          </text>
        </g>
      ))}

      {/* P&L line */}
      <path d={linePath} fill="none" stroke="#6366f1" strokeWidth={2.5} strokeLinejoin="round" />

      {/* Y-axis labels */}
      {yTickValues.map((v, i) => (
        <text
          key={`yl-${i}`}
          x={pad.left - 8}
          y={yScale(v) + 4}
          textAnchor="end"
          fill="#6b7280"
          fontSize={9}
          fontFamily="JetBrains Mono, monospace"
        >
          {formatPnl(v)}
        </text>
      ))}

      {/* X-axis labels */}
      {xTickValues.map((v, i) => (
        <text
          key={`xl-${i}`}
          x={xScale(v)}
          y={H - 8}
          textAnchor="middle"
          fill="#6b7280"
          fontSize={9}
          fontFamily="JetBrains Mono, monospace"
        >
          ${formatK(v)}
        </text>
      ))}

      {/* Axis titles */}
      <text x={W / 2} y={H} textAnchor="middle" fill="#4b5563" fontSize={10}>
        BTC Price at Expiry
      </text>
      <text
        x={10}
        y={H / 2}
        textAnchor="middle"
        fill="#4b5563"
        fontSize={10}
        transform={`rotate(-90, 10, ${H / 2})`}
      >
        P&L (USDT)
      </text>
    </svg>
  )
}

function formatK(v: number): string {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`
  return v.toFixed(0)
}

function formatPnl(v: number): string {
  if (Math.abs(v) >= 1000) return `${(v >= 0 ? '+' : '')}${(v / 1000).toFixed(1)}K`
  return `${v >= 0 ? '+' : ''}${v.toFixed(0)}`
}

/* ─── Helpers ─── */

function fmtMoney(v?: number | null): string {
  if (v == null || isNaN(v)) return '—'
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(v?: number | null): string {
  if (v == null || isNaN(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function formatDate(s?: string | null): string {
  if (!s) return '—'
  try {
    const d = new Date(s)
    if (isNaN(d.getTime())) return s
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return s
  }
}

function stratColor(t: string) {
  if (t.includes('BULL') || t.includes('CALL_SPREAD')) return 'text-emerald-400'
  if (t.includes('BEAR') || t.includes('PUT_SPREAD')) return 'text-rose-400'
  if (t.includes('STRADDLE') || t.includes('STRANGLE')) return 'text-purple-400'
  if (t.includes('IRON')) return 'text-sky-400'
  return 'text-amber-400'
}

/* ─── Component ─── */

export function StrategyDetailModal({ strategy, onClose }: StrategyDetailModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose()
  }, [onClose])

  const legs = useMemo(() => getLegs(strategy), [strategy])
  const maxProfit = strategy.max_profit ?? 0
  const maxLoss = strategy.max_loss ?? 0
  const netDebit = strategy.net_debit ?? 0
  const isCredit = strategy.is_credit ?? (netDebit < 0)
  const netCredit = strategy.net_credit ?? (isCredit ? -netDebit : 0)
  const riskReward = (isCredit ? netCredit : netDebit) > 0 ? (maxProfit / (isCredit ? netCredit : netDebit)) : 0
  const strike = strategy.strike ?? 0

  // Use backend breakeven if available, otherwise derive from payoff curve
  const backendBE = strategy.breakeven ?? null
  const payoffs = useMemo(() => computePayoffCurve(strategy), [strategy])
  const breakevens = useMemo(() => findBreakevens(payoffs), [payoffs])
  const breakeven = backendBE ?? (breakevens.length > 0 ? breakevens[0] : null)

  const evPct = strategy.ev_pct ?? (strategy.ev && strategy.mark ? ((strategy.ev / strategy.mark) * 100) : null)

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-end justify-end bg-black/60 backdrop-blur-sm sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Panel */}
      <div
        ref={panelRef}
        className="relative flex max-h-[95vh] w-full flex-col overflow-hidden rounded-t-2xl border border-ev-border bg-ev-card shadow-2xl sm:mx-4 sm:max-h-[90vh] sm:w-[540px] sm:rounded-2xl md:max-w-[600px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ev-border px-5 py-4">
          <div>
            <h2 className={`text-lg font-bold ${stratColor(strategy.strategy_type)}`}>
              {strategy.strategy_type?.replace(/_/g, ' ')}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {strategy.symbol ?? '—'} · Strike {strike ? `$${strike.toLocaleString()}` : '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-ev-surface hover:text-white"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Payoff Diagram */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Payoff Diagram</h3>
            <div className="rounded-xl border border-ev-border bg-ev-dark/50 p-3">
              <PayoffChart strategy={strategy} />
            </div>
          </section>

          {/* Trade Details Card */}
          <section>
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Trade Details</h3>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                isCredit
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/40'
                  : 'bg-blue-600/20 text-blue-400 border border-blue-600/40'
              }`}>
                {isCredit ? 'NET CREDIT' : 'NET DEBIT'}
              </span>
              <span className="font-mono text-xs text-gray-300">
                ${isCredit ? netCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : netDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="space-y-2">
              {legs.map((leg, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                    leg.action === 'BUY'
                      ? 'border-emerald-700/40 bg-emerald-900/20'
                      : 'border-rose-700/40 bg-rose-900/20'
                  }`}
                >
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                    leg.action === 'BUY'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-rose-600 text-white'
                  }`}>
                    {leg.action}
                  </span>
                  <span className="font-semibold text-white">{leg.type}</span>
                  <span className="font-mono text-gray-300">K: ${leg.strike.toLocaleString()}</span>
                  <span className="ml-auto font-mono text-gray-400">
                    @ ${leg.premium.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Key Metrics Grid */}
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Key Metrics</h3>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <MetricCard label="Max Profit" value={fmtMoney(maxProfit)} color="text-emerald-400" />
              <MetricCard label="Max Loss" value={fmtMoney(maxLoss)} color="text-red-400" />
              <MetricCard
                label="Risk / Reward"
                value={riskReward > 0 ? `1 : ${riskReward.toFixed(1)}` : '—'}
                color="text-white"
              />
              <MetricCard label="Breakeven" value={breakeven ? `$${breakeven.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'} color="text-amber-400" />
              <MetricCard label="EV (Raw)" value={fmtMoney(strategy.ev)} color={(strategy.ev ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'} />
              <MetricCard label="EV After Fee" value={fmtMoney(strategy.ev_after_fee)} color={(strategy.ev_after_fee ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'} sub={evPct != null ? `EV%: ${evPct.toFixed(2)}%` : undefined} />
              <MetricCard label="Total Fees" value={fmtMoney(strategy.total_fees)} color="text-gray-400" />
              <MetricCard label="Prob. of Profit" value={fmtPct(strategy.probability)} color="text-purple-400" />
              <MetricCard label="DTE" value={strategy.dte != null ? String(strategy.dte) : '—'} color="text-sky-400" />
              <MetricCard label="Expiry" value={formatDate(strategy.expiry)} color="text-gray-300" />
              <MetricCard label="Moneyness" value={strategy.moneyness ?? '—'} color={
                strategy.moneyness === 'ITM' ? 'text-amber-400' :
                strategy.moneyness === 'ATM' ? 'text-purple-400' : 'text-blue-400'
              } />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-ev-border px-5 py-3 text-center text-[10px] text-gray-600">
          Click outside or press Esc to close
        </div>
      </div>
    </div>
  )
}

function MetricCard({ label, value, color, sub }: {
  label: string
  value: string
  color: string
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-ev-border bg-ev-surface/50 px-3 py-2.5">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${color}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-gray-500">{sub}</div>}
    </div>
  )
}
